import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { GraduationCap, Lock, X, Sparkles, ArrowRight, Play, BookOpen, Sun, Moon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const Login = () => {
    const { students, login, loginAsTeacher } = useAuth();
    const [showStudentModal, setShowStudentModal] = useState(false);
    const [showTeacherLogin, setShowTeacherLogin] = useState(false);
    const [redirectPath, setRedirectPath] = useState('/');
    const [pin, setPin] = useState('');
    const [error, setError] = useState(false);
    const [isDarkMode, setIsDarkMode] = useState(true);
    const navigate = useNavigate();

    const handleTeacherLogin = (e) => {
        e.preventDefault();
        const validPin = localStorage.getItem('geoceuta_teacher_pin') || import.meta.env.VITE_TEACHER_PIN || '1234';
        if (pin === validPin) {
            loginAsTeacher();
            setShowTeacherLogin(false);
            navigate('/teacher');
        } else {
            setError(true);
            setPin('');
            setTimeout(() => setError(false), 2000);
        }
    };

    const openStudentModalWithRedirect = (path) => {
        setRedirectPath(path);
        setShowStudentModal(true);
    };

    return (
        <div className={`h-screen w-screen transition-colors duration-500 bg-gradient-to-br flex flex-col justify-between relative overflow-hidden font-sans p-0 m-0 ${
            isDarkMode 
                ? 'from-[#003D7C] via-[#052e59] to-[#011a33] text-white' 
                : 'from-[#FFFFFF] via-[#F4F9FF] to-[#E8F2FD] text-gray-800'
        }`}>
            {/* Fondo decorativo con figuras geométricas sutiles */}
            <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none select-none">
                {/* Aura de luces */}
                <div className={`absolute top-[-10%] left-[-10%] w-[35vw] h-[35vw] rounded-full blur-[100px] transition-colors duration-500 ${
                    isDarkMode ? 'bg-[#FFCC00]/10' : 'bg-[#FFCC00]/15'
                }`} />
                <div className={`absolute bottom-[-10%] right-[-10%] w-[30vw] h-[30vw] rounded-full blur-[90px] transition-colors duration-500 ${
                    isDarkMode ? 'bg-[#F72585]/10' : 'bg-[#F72585]/5'
                }`} />
                
                {/* Triángulo */}
                <svg className={`absolute top-[15%] left-[8%] w-16 h-16 transition-colors duration-500 animate-[spin_40s_linear_infinite] ${
                    isDarkMode ? 'text-[#FFCC00]/5' : 'text-[#FFCC00]/15'
                }`} viewBox="0 0 100 100" fill="currentColor">
                    <polygon points="50,15 90,85 10,85" />
                </svg>

                {/* Hexágono */}
                <svg className={`absolute bottom-[22%] right-[8%] w-24 h-24 transition-colors duration-500 animate-[spin_60s_linear_infinite] ${
                    isDarkMode ? 'text-white/5' : 'text-[#003D7C]/10'
                }`} viewBox="0 0 100 100" fill="none" stroke="currentColor" strokeWidth="4">
                    <polygon points="50,5 95,25 95,75 50,95 5,75 5,25" />
                </svg>

                {/* Círculo concéntrico */}
                <svg className={`absolute top-[35%] right-[5%] w-20 h-20 transition-colors duration-500 ${
                    isDarkMode ? 'text-white/5' : 'text-[#003D7C]/8'
                }`} viewBox="0 0 100 100" fill="none" stroke="currentColor" strokeWidth="3">
                    <circle cx="50" cy="50" r="40" strokeDasharray="4,4" />
                    <circle cx="50" cy="50" r="25" />
                </svg>

                {/* Cuadrado rotado */}
                <svg className={`absolute bottom-[20%] left-[10%] w-14 h-14 transition-colors duration-500 rotate-[30deg] ${
                    isDarkMode ? 'text-[#70E000]/5' : 'text-[#70E000]/15'
                }`} viewBox="0 0 100 100" fill="none" stroke="currentColor" strokeWidth="4">
                    <rect x="15" y="15" width="70" height="70" rx="8" />
                </svg>

                {/* Líneas angulares */}
                <svg className={`absolute top-[10%] right-[22%] w-32 h-16 transition-colors duration-500 ${
                    isDarkMode ? 'text-white/5' : 'text-[#003D7C]/8'
                }`} viewBox="0 0 200 100" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="10" y1="10" x2="190" y2="10" />
                    <line x1="10" y1="30" x2="190" y2="30" strokeDasharray="10,5" />
                    <line x1="50" y1="10" x2="150" y2="90" />
                </svg>

                {/* Cuadrícula geométrica de fondo */}
                <div className={`absolute inset-0 transition-all duration-500 bg-[size:40px_40px] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_50%,#000_70%,transparent_100%)] ${
                    isDarkMode 
                        ? 'bg-[linear-gradient(rgba(255,255,255,0.015)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.015)_1px,transparent_1px)]' 
                        : 'bg-[linear-gradient(rgba(0,61,124,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(0,61,124,0.02)_1px,transparent_1px)]'
                }`} />
            </div>

            {/* Header de la Landing */}
            <header className="w-full px-6 py-4 flex justify-between items-center z-10 shrink-0">
                <div className="flex items-center gap-2.5">
                    <img 
                        src="/logo_planeta_primaria.svg" 
                        alt="Mundo Primaria Logo" 
                        className={`w-10 h-10 rounded-lg shadow-lg border-2 ${
                            isDarkMode ? 'border-[#FFCC00]' : 'border-[#FFCC00] bg-[#FFCC00]'
                        }`} 
                    />
                    <div>
                        <span className={`text-xl font-black tracking-wider transition-colors duration-500 block leading-none mb-0.5 ${
                            isDarkMode ? 'text-[#FFCC00]' : 'text-[#003D7C]'
                        }`}>MUNDO PRIMARIA</span>
                        <span className={`text-[10px] font-bold tracking-widest block uppercase leading-none transition-colors duration-500 ${
                            isDarkMode ? 'text-blue-200' : 'text-gray-500'
                        }`}>GeoCeuta</span>
                    </div>
                </div>

                <div className="flex items-center gap-3">
                    {/* Botón para cambiar Modo Oscuro/Claro */}
                    <button
                        onClick={() => setIsDarkMode(!isDarkMode)}
                        title={isDarkMode ? "Modo Claro" : "Modo Oscuro"}
                        className={`p-2 rounded-full border transition-all ${
                            isDarkMode 
                                ? 'bg-white/5 hover:bg-white/15 border-white/15 text-[#FFCC00]' 
                                : 'bg-black/5 hover:bg-black/10 border-black/10 text-[#003D7C]'
                        }`}
                    >
                        {isDarkMode ? <Sun size={16} /> : <Moon size={16} />}
                    </button>

                    <button
                        onClick={() => setShowTeacherLogin(true)}
                        className={`flex items-center gap-1.5 px-4 py-2 rounded-full border transition-all font-bold text-xs ${
                            isDarkMode 
                                ? 'bg-white/10 hover:bg-white/20 border-white/25 text-blue-100 hover:text-white' 
                                : 'bg-[#003D7C]/5 hover:bg-[#003D7C]/10 border-[#003D7C]/15 text-[#003D7C]'
                        }`}
                    >
                        <Lock size={13} />
                        Profesorado
                    </button>
                </div>
            </header>

            {/* Contenido Principal / Hero Section */}
            <main className="flex-1 flex flex-col items-center justify-center px-6 text-center z-10 max-h-[45vh]">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="max-w-2xl"
                >
                    <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full border transition-colors duration-500 font-bold text-[11px] uppercase tracking-wider mb-4 ${
                        isDarkMode 
                            ? 'bg-[#FFCC00]/10 border-[#FFCC00]/30 text-[#FFCC00]' 
                            : 'bg-[#003D7C]/5 border-[#003D7C]/10 text-[#003D7C]'
                    }`}>
                        <Sparkles size={13} className={isDarkMode ? 'text-white' : 'text-brand-orange'} />
                        Plataforma Educativa de Geometría
                    </div>

                    <h1 className={`text-3xl md:text-5xl font-black tracking-tight leading-tight mb-3 transition-colors duration-500 ${
                        isDarkMode ? 'text-white' : 'text-[#003D7C]'
                    }`}>
                        ¡Aprende Geometría de forma {' '}
                        {isDarkMode ? (
                            <span className="text-[#FFCC00] underline decoration-4 decoration-brand-orange underline-offset-4">Divertida</span>
                        ) : (
                            <span className="px-3.5 py-0.5 bg-[#FFCC00] text-[#003D7C] rounded-2xl shadow-sm inline-block transform -rotate-1">Divertida</span>
                        )}
                        !
                    </h1>

                    <p className={`text-sm md:text-base font-medium mb-6 max-w-xl mx-auto leading-relaxed transition-colors duration-500 ${
                        isDarkMode ? 'text-blue-100/80' : 'text-gray-600'
                    }`}>
                        Explora líneas, polígonos y cuerpos geométricos en un entorno interactivo diseñado para primaria. ¡Dibuja, mide y diviértete con GeoCeuta!
                    </p>

                    <div className="flex justify-center items-center">
                        <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => openStudentModalWithRedirect('/')}
                            className={`flex items-center justify-center gap-2 px-8 py-3.5 rounded-xl font-black text-md transition-all cursor-pointer ${
                                isDarkMode 
                                    ? 'bg-[#FFCC00] hover:bg-[#e6b800] text-[#003D7C] shadow-[0_4px_20px_rgb(255,204,0,0.25)]' 
                                    : 'bg-[#003D7C] hover:bg-[#002b57] text-[#FFCC00] shadow-[0_4px_20px_rgba(0,61,124,0.25)]'
                            }`}
                        >
                            <Play size={16} fill={isDarkMode ? "#003D7C" : "#FFCC00"} />
                            Comenzar Aventura
                        </motion.button>
                    </div>
                </motion.div>
            </main>

            {/* Sección de Accesos Directos */}
            <section className={`border-t transition-colors duration-500 py-6 px-6 z-10 shrink-0 ${
                isDarkMode ? 'bg-white/5 border-white/10' : 'bg-white/60 backdrop-blur-md border-gray-200'
            }`}>
                <div className="max-w-5xl mx-auto">
                    <h2 className={`text-center text-xs font-black mb-4 uppercase tracking-widest leading-none transition-colors duration-500 ${
                        isDarkMode ? 'text-blue-200' : 'text-[#003D7C]/60'
                    }`}>Accesos Directos a Módulos</h2>
                    <div className="grid md:grid-cols-3 gap-4 text-left">
                        {/* Tarjeta 1 */}
                        <motion.div 
                            whileHover={{ scale: 1.02, backgroundColor: isDarkMode ? "rgba(255, 255, 255, 0.08)" : "#FFFFFF" }}
                            onClick={() => openStudentModalWithRedirect('/units')}
                            className={`p-4 rounded-xl border transition-all cursor-pointer flex flex-col justify-between group shadow-md hover:shadow-lg ${
                                isDarkMode 
                                    ? 'bg-white/5 border-white/10 hover:border-[#FFCC00]/50' 
                                    : 'bg-white/80 border-gray-100 hover:border-[#FFCC00]/50'
                            }`}
                        >
                            <div>
                                <div className="flex items-center gap-3 mb-2">
                                    <div className="w-8 h-8 rounded-lg bg-brand-green/20 text-[#70E000] flex items-center justify-center text-lg font-bold">📏</div>
                                    <h3 className={`text-base font-bold transition-colors leading-none ${
                                        isDarkMode ? 'text-white group-hover:text-[#FFCC00]' : 'text-gray-800 group-hover:text-[#003D7C]'
                                    }`}>Unidades Didácticas</h3>
                                </div>
                                <p className={`text-xs leading-normal mb-3 transition-colors ${
                                    isDarkMode ? 'text-blue-100/70' : 'text-gray-500'
                                }`}>Lecciones sobre líneas, polígonos y figuras tridimensionales curriculares.</p>
                            </div>
                            <span className={`font-black text-[11px] flex items-center gap-1 group-hover:translate-x-1.5 transition-all leading-none ${
                                isDarkMode ? 'text-[#FFCC00]' : 'text-[#003D7C] group-hover:text-brand-orange'
                            }`}>
                                Ir a Aprender <ArrowRight size={13} />
                            </span>
                        </motion.div>

                        {/* Tarjeta 2 */}
                        <motion.div 
                            whileHover={{ scale: 1.02, backgroundColor: isDarkMode ? "rgba(255, 255, 255, 0.08)" : "#FFFFFF" }}
                            onClick={() => openStudentModalWithRedirect('/whiteboard')}
                            className={`p-4 rounded-xl border transition-all cursor-pointer flex flex-col justify-between group shadow-md hover:shadow-lg ${
                                isDarkMode 
                                    ? 'bg-white/5 border-white/10 hover:border-[#FFCC00]/50' 
                                    : 'bg-white/80 border-gray-100 hover:border-[#FFCC00]/50'
                            }`}
                        >
                            <div>
                                <div className="flex items-center gap-3 mb-2">
                                    <div className="w-8 h-8 rounded-lg bg-brand-pink/20 text-brand-pink flex items-center justify-center text-lg font-bold">🎨</div>
                                    <h3 className={`text-base font-bold transition-colors leading-none ${
                                        isDarkMode ? 'text-white group-hover:text-[#FFCC00]' : 'text-gray-800 group-hover:text-[#003D7C]'
                                    }`}>Pizarra Libre</h3>
                                </div>
                                <p className={`text-xs leading-normal mb-3 transition-colors ${
                                    isDarkMode ? 'text-blue-100/70' : 'text-gray-500'
                                }`}>Dibuja y clasifica figuras geométricas usando herramientas dinámicas.</p>
                            </div>
                            <span className={`font-black text-[11px] flex items-center gap-1 group-hover:translate-x-1.5 transition-all leading-none ${
                                isDarkMode ? 'text-[#FFCC00]' : 'text-[#003D7C] group-hover:text-brand-orange'
                            }`}>
                                Abrir Pizarra <ArrowRight size={13} />
                            </span>
                        </motion.div>

                        {/* Tarjeta 3 */}
                        <motion.div 
                            whileHover={{ scale: 1.02, backgroundColor: isDarkMode ? "rgba(255, 255, 255, 0.08)" : "#FFFFFF" }}
                            onClick={() => openStudentModalWithRedirect('/geoboard')}
                            className={`p-4 rounded-xl border transition-all cursor-pointer flex flex-col justify-between group shadow-md hover:shadow-lg ${
                                isDarkMode 
                                    ? 'bg-white/5 border-white/10 hover:border-[#FFCC00]/50' 
                                    : 'bg-white/80 border-gray-100 hover:border-[#FFCC00]/50'
                            }`}
                        >
                            <div>
                                <div className="flex items-center gap-3 mb-2">
                                    <div className="w-8 h-8 rounded-lg bg-brand-orange/20 text-brand-orange flex items-center justify-center text-lg font-bold">🔷</div>
                                    <h3 className={`text-base font-bold transition-colors leading-none ${
                                        isDarkMode ? 'text-white group-hover:text-[#FFCC00]' : 'text-gray-800 group-hover:text-[#003D7C]'
                                    }`}>Geoplano Virtual</h3>
                                </div>
                                <p className={`text-xs leading-normal mb-3 transition-colors ${
                                    isDarkMode ? 'text-blue-100/70' : 'text-gray-500'
                                }`}>Estira gomas virtuales para comprender perímetros y áreas dinámicamente.</p>
                            </div>
                            <span className={`font-black text-[11px] flex items-center gap-1 group-hover:translate-x-1.5 transition-all leading-none ${
                                isDarkMode ? 'text-[#FFCC00]' : 'text-[#003D7C] group-hover:text-brand-orange'
                            }`}>
                                Abrir Geoplano <ArrowRight size={13} />
                            </span>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* MODAL DE SELECCIÓN DE ALUMNOS */}
            <AnimatePresence>
                {showStudentModal && (
                    <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center p-4 z-50">
                        <motion.div 
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.9, opacity: 0 }}
                            className="bg-white text-gray-800 rounded-3xl p-8 max-w-4xl w-full max-h-[85vh] overflow-y-auto relative border-4 border-[#FFCC00]"
                        >
                            <button 
                                onClick={() => setShowStudentModal(false)}
                                className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 text-gray-500 transition-colors"
                            >
                                <X size={24} />
                            </button>

                            <div className="text-center mb-8">
                                <span className="text-3xl font-black text-[#003D7C] block">¿Quién va a jugar hoy?</span>
                                <p className="text-gray-500 font-medium mt-1">Selecciona tu avatar para entrar a tu perfil</p>
                            </div>

                            {students.length > 0 ? (
                                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6">
                                    {students.map((student) => (
                                        <button
                                            key={student.id}
                                            onClick={() => {
                                                login(student);
                                                setShowStudentModal(false);
                                                navigate(redirectPath);
                                            }}
                                            className="group flex flex-col items-center p-4 bg-gray-50 rounded-2xl border-2 border-transparent hover:border-[#003D7C] hover:bg-white hover:shadow-xl transition-all transform hover:-translate-y-1"
                                        >
                                            <div className="w-20 h-20 rounded-full overflow-hidden mb-3 border-4 border-gray-200 group-hover:border-[#003D7C] transition-colors">
                                                <img
                                                    src={student.avatar}
                                                    alt={student.name}
                                                    className="w-full h-full object-cover"
                                                />
                                            </div>
                                            <span className="font-bold text-gray-700 group-hover:text-[#003D7C] text-md truncate max-w-full">
                                                {student.name}
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-12">
                                    <p className="text-gray-500 font-medium">No hay alumnos configurados aún.</p>
                                </div>
                            )}
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            {/* MODAL DE ACCESO PROFESOR */}
            <AnimatePresence>
                {showTeacherLogin && (
                    <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center p-4 z-50">
                        <motion.div 
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.9, opacity: 0 }}
                            className="bg-white text-gray-800 rounded-3xl p-8 max-w-md w-full relative border-4 border-brand-orange"
                        >
                            <button 
                                onClick={() => { setShowTeacherLogin(false); setPin(''); setError(false); }}
                                className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 text-gray-500 transition-colors"
                            >
                                <X size={24} />
                            </button>

                            <h2 className="text-2xl font-black text-[#003D7C] text-center mb-6">Acceso Docente</h2>
                            <form onSubmit={handleTeacherLogin} className="space-y-4">
                                <div>
                                    <input
                                        type="password"
                                        value={pin}
                                        onChange={(e) => setPin(e.target.value)}
                                        placeholder="PIN de acceso"
                                        className={`w-full text-center text-3xl tracking-widest p-4 rounded-xl border-2 outline-none transition-all ${error
                                            ? 'border-red-400 bg-red-50 text-red-500 placeholder-red-300 animate-shake'
                                            : 'border-gray-200 focus:border-[#003D7C] focus:ring-4 focus:ring-[#003D7C]/10'
                                            }`}
                                        maxLength="4"
                                        autoFocus
                                    />
                                    {error && <p className="text-center text-red-500 text-sm mt-2 font-bold">PIN Incorrecto</p>}
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <button
                                        type="button"
                                        onClick={() => { setShowTeacherLogin(false); setPin(''); setError(false); }}
                                        className="p-3 rounded-xl font-bold text-gray-500 hover:bg-gray-100 transition-colors"
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        type="submit"
                                        className="p-3 rounded-xl font-bold bg-[#003D7C] text-white shadow-lg hover:shadow-xl hover:scale-105 transition-all"
                                    >
                                        Entrar
                                    </button>
                                </div>
                            </form>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default Login;
